/**
 * Bot gunnery and helmsmanship. A pure decision function: given what the bot
 * can see this tick, produce the same `ShipInput` a human would send. Bots go
 * through the same intake as players — the arena clamps and steps their input
 * exactly like anyone else's, so a bot cannot do anything a client could not.
 *
 * Deterministic: all randomness comes from the arena's PRNG.
 *
 * A bot knows its team: the arena hands it enemy ships only, so it never
 * shoots a friend, and it keeps the **lane** it spawned in (its z in the spawn
 * line) while advancing toward the enemy line along x, which spreads a team out
 * instead of letting six bots converge on one point. Below `RETREAT_HP` it
 * turns away and runs, still firing whatever will bear.
 *
 * It also only knows what its team can see. The spotted set (`vision.ts`) is
 * passed in rather than baked into the target list so the rule is visible and
 * testable here, where the shooting decision is made: a bot firing on a ship
 * no human on its side could see would make concealment worth nothing.
 *
 * It sails for the objectives (`objectives.ts`) rather than straight down its
 * lane: every few seconds it picks the nearest ring its team does not hold and
 * keeps station in it, orbiting the middle rather than parking, and it breaks
 * off only for an enemy inside `ZONE_BREAK_RANGE`. Anything further away is
 * shot at from where it is standing — a bot that chased every contact to the
 * horizon would leave the zone it was sent to hold, which is the mistake the
 * whole feature exists to make visible.
 *
 * Losing sight of a target does not wipe the bot's memory. It holds the last
 * bearing it had for `MEMORY_TICKS` — long enough to keep the turn it was in
 * the middle of and to look like a captain who saw something go dark rather
 * than a machine that forgot — and holds its fire while it does, because there
 * is nothing there to hit and a salvo would only give its own position away.
 */
import { type Island, type IslandLayout, avoidLand, losBlocked } from './islands';
import type { Prng } from './prng';
import { ARENA_HALF } from './protocol';
import type { ShipSpec } from './ship-spec';
import {
  type ShipInput,
  type ShipState,
  angleDelta,
  flightTime,
  maxShellRange,
  readyTorpedoMounts,
  torpedoMountCanBear,
  wrapAngle,
} from './simulation';
import { torpedoLead } from './torpedo-solution';

export interface BotBrain {
  /** The z of the lane this bot holds while it has nobody to fight. */
  laneZ: number;
  /** The ship this bot last had in sight, or null. */
  targetId: string | null;
  /** Tick at which that target went dark; −1 while it is still in sight. */
  lostTick: number;
  /** The bearing to it when it went, held while the memory lasts. */
  lostBearing: number;
  /** +1 advances toward +x, −1 toward −x: the direction of the enemy line. */
  advanceX: 1 | -1;
  /** +1 circles a target clockwise, −1 the other way. */
  side: 1 | -1;
  /** Tick at which the brain reconsiders its side. */
  nextThink: number;
  /**
   * Which of two rings this bot takes when they are much of a muchness
   * (`chooseStation`): the second-nearest for an odd seat, the nearest for an
   * even one. Its seat in the line, so half a line goes each way.
   */
  spread: number;
  /** The zone it is holding: centre, radius, and whether it has one at all. */
  station: { x: number; z: number; radius: number } | null;
  /** Tick at which it reconsiders which zone that is. */
  nextZoneThink: number;
  /**
   * How good a gunner this bot is, 0…1. At 1 it lays the guns on the lead
   * exactly and fires the tick they bear; at 0 it is a couple of degrees
   * out either way, fires on a third of its chances and thinks slowly.
   * The arena sets it from the humans present (`skillForTier`).
   */
  skill: number;
}

/** The most a bot's lay is out by at skill 0, radians: about 2.6°. */
const AIM_ERROR_MAX = 0.045;

/**
 * Bot skill for the tier of ship the humans brought: a tier I captain meets
 * bots that miss, a tier IV one meets bots that do not.
 */
export function skillForTier(tier: number): number {
  const t = Math.min(4, Math.max(1, Number.isFinite(tier) ? tier : 1));
  return Math.min(1, 0.45 + (t - 1) * 0.16);
}

/** The tier in a hull id like `cruiser_t3`; 1 for a stock hull. */
export function tierOfSpecId(specId: string): number {
  const m = /_t(\d)$/.exec(specId);
  return m ? Number(m[1]) : 1;
}

/**
 * A capture zone as a bot sees it. `held` is "held by *this* bot's team" —
 * the arena resolves the owner, because whose ring it is is the only part of
 * the question a helm has to answer.
 */
export interface BotZone {
  x: number;
  z: number;
  radius: number;
  held: boolean;
}

export interface BotTarget {
  id: string;
  state: ShipState;
  /** Velocity estimate used for lead. */
  vx: number;
  vz: number;
}

/** Bots try to fight between these distances. */
export const ENGAGE_MIN = 1500;
export const ENGAGE_MAX = 3000;
export const DETECT_RANGE = 4500;
/** Fraction of max HP below which a bot disengages. */
export const RETREAT_HP = 0.3;
/** How far a hurt bot looks for an island to hide behind. */
export const COVER_SEARCH_RANGE = 2600;
/** Metres past the shore the lee point stands, so the hull is off the beach. */
export const COVER_STANDOFF = 160;
/** Within this of its lee point, and out of sight, a hurt bot holds. */
export const COVER_HOLD_RADIUS = 220;
/** Ticks a bot holds the bearing of a target that went dark — three seconds at 20 Hz. */
export const MEMORY_TICKS = 60;

/**
 * Station-keeping. A bot reconsiders which ring it wants every
 * `ZONE_THINK_TICKS` (three seconds) — often enough to react to a zone
 * flipping, rare enough that twelve bots cost twelve decisions a second
 * instead of two hundred and forty — and abandons the station only for an
 * enemy inside `ZONE_BREAK_RANGE`, which is comfortably inside gun range but
 * well short of `DETECT_RANGE`: a contact on the far side of the map is
 * something to shoot at, not something to steam toward.
 *
 * Inside the ring it orbits at `ZONE_ORBIT_THROTTLE` rather than stopping. A
 * ship dead in the water is a free hit, and a circle 600 m across is big
 * enough to keep way on inside.
 */
export const ZONE_THINK_TICKS = 60;
export const ZONE_BREAK_RANGE = 2600;
export const ZONE_ORBIT_THROTTLE = 0.45;

/**
 * Torpedo discipline. A bot launches at a target inside this fraction of the
 * fish's fuel — short of the full range, because a salvo that arrives with no
 * fuel left arrives at wherever the target was three minutes ago — and never
 * inside `TORPEDO_MIN_FRACTION` of it, where the warhead has barely armed and
 * the fan has had no room to spread.
 *
 * The check only runs on a tick where a mount is actually loaded, so the
 * geometry is paid for once a minute per launcher rather than twenty times a
 * second: everything below the reload gate is free.
 */
export const TORPEDO_MAX_FRACTION = 0.8;
export const TORPEDO_MIN_FRACTION = 0.1;

export function createBotBrain(rng: Prng, laneZ = 0, advanceX: 1 | -1 = 1, skill = 0.7, spread = 0): BotBrain {
  return {
    laneZ,
    spread,
    targetId: null,
    lostTick: -1,
    lostBearing: 0,
    advanceX,
    skill: Math.min(1, Math.max(0, skill)),
    side: rng.next() < 0.5 ? 1 : -1,
    nextThink: 0,
    station: null,
    nextZoneThink: 0,
  };
}

/**
 * Two rings within this ratio of each other's distance are "much of a
 * muchness": on a chart where every ring is as far from the line as every
 * other (the atoll), a whole line would otherwise sail for the same one and
 * leave the rest unclaimed. On the classic chart a team's near ring is well
 * under half the distance of the middle one, so nothing changes there.
 */
const SPREAD_RATIO = 1.4;

/**
 * Pick the ring to hold: the nearest one this team does not own, and failing
 * that the nearest one at all — a team that holds everything still has to
 * stand in something to keep it. When the second-nearest unheld ring is
 * nearly as close as the nearest, the odd seats take it instead, so a line
 * spreads over two rings rather than piling into one.
 */
function chooseStation(brain: BotBrain, self: ShipState, zones: readonly BotZone[], tick: number): void {
  if (tick < brain.nextZoneThink && brain.station !== null) return;
  brain.nextZoneThink = tick + ZONE_THINK_TICKS;
  let best: BotZone | null = null;
  let bestD = Infinity;
  let second: BotZone | null = null;
  let secondD = Infinity;
  let fallback: BotZone | null = null;
  let fallbackD = Infinity;
  for (const zone of zones) {
    const d = (zone.x - self.x) ** 2 + (zone.z - self.z) ** 2;
    if (d < fallbackD) {
      fallbackD = d;
      fallback = zone;
    }
    if (zone.held) continue;
    if (d < bestD) {
      second = best;
      secondD = bestD;
      bestD = d;
      best = zone;
    } else if (d < secondD) {
      secondD = d;
      second = zone;
    }
  }
  let pick = best ?? fallback;
  if (best && second && brain.spread % 2 === 1 && secondD <= bestD * SPREAD_RATIO * SPREAD_RATIO) pick = second;
  brain.station = pick ? { x: pick.x, z: pick.z, radius: pick.radius } : null;
}

/**
 * Somewhere to hide: the point in the lee of the nearest island that the
 * island actually screens from `threat`. For each island within
 * `COVER_SEARCH_RANGE`, the lee is the shore point on the far side from the
 * threat pushed `COVER_STANDOFF` out; it counts only if the line from the
 * threat to it is blocked (`losBlocked`), so a low rock the threat sees
 * straight over is not cover. Nearest lee wins. `null` on open water.
 * Pure; the bots use it when hurt, and the test asks it directly.
 */
export function coverPoint(layout: IslandLayout, self: ShipState, threat: { x: number; z: number }): { x: number; z: number; island: Island } | null {
  let best: { x: number; z: number; island: Island } | null = null;
  let bestD = COVER_SEARCH_RANGE;
  for (const island of layout.islands) {
    const dx = island.x - threat.x;
    const dz = island.z - threat.z;
    const len = Math.hypot(dx, dz);
    if (len < 1e-3) continue;
    const r = Math.max(island.rx, island.rz) + COVER_STANDOFF;
    const x = island.x + (dx / len) * r;
    const z = island.z + (dz / len) * r;
    const d = Math.hypot(x - self.x, z - self.z);
    if (d >= bestD) continue;
    if (!losBlocked(layout, threat.x, threat.z, x, z)) continue;
    best = { x, z, island };
    bestD = d;
  }
  return best;
}

/**
 * Helm and throttle for keeping station. Outside the ring it steams for the
 * middle; inside it turns beam-on and circles, which keeps the hull moving and
 * the broadside pointed outward.
 */
function stationHelm(brain: BotBrain, self: ShipState): { bearing: number; throttle: number } {
  const station = brain.station!;
  const centre = bearingTo(self, station.x, station.z);
  const d = Math.hypot(station.x - self.x, station.z - self.z);
  if (d > station.radius * 0.7) return { bearing: centre, throttle: 0.9 };
  return { bearing: wrapAngle(centre + brain.side * (Math.PI / 2)), throttle: ZONE_ORBIT_THROTTLE };
}

export function bearingTo(from: ShipState, x: number, z: number): number {
  return Math.atan2(x - from.x, z - from.z);
}

/**
 * Helm that brings the hull onto `bearing`. Negated because positive rudder is
 * starboard helm and starboard helm takes yaw *down* — see `stepShip`.
 */
export function steerTo(self: ShipState, bearing: number): number {
  const d = -angleDelta(self.yaw, bearing);
  const r = d / 0.6;
  return r < -1 ? -1 : r > 1 ? 1 : r;
}

/** Turns a desired heading back inside the arena square when the ship is near an edge. */
export function keepInside(self: ShipState, desired: number): number {
  const margin = ARENA_HALF - 400;
  const outX = Math.abs(self.x) > margin;
  const outZ = Math.abs(self.z) > margin;
  if (!outX && !outZ) return desired;
  // Head for the centre when over an edge; the arena is where the fight is.
  return bearingTo(self, 0, 0);
}

export function decideBot(
  brain: BotBrain,
  self: ShipState,
  spec: ShipSpec,
  targets: readonly BotTarget[],
  tick: number,
  rng: Prng,
  /** Ids the bot's own team can see. Omitted (or null) means a clear sea — the offline callers and the older tests. */
  spotted: ReadonlySet<string> | null = null,
  /** The capture zones, as this bot's team sees them. Omitted means the old lane-advance behaviour. */
  zones: readonly BotZone[] | null = null,
  /** The land, if any. A bot steers round it before it steers for anything else. */
  layout: IslandLayout | null = null,
): ShipInput {
  let input = decideCourse(brain, self, spec, targets, tick, rng, spotted, zones);
  if (!layout || layout.islands.length === 0) return input;
  // Hurt, with somewhere to hide: run for the lee of an island rather than
  // straight away down the enemy's guns. The guns still lay on the target.
  if (self.hp < spec.hp * RETREAT_HP && brain.targetId !== null) {
    const target = targets.find((t) => t.id === brain.targetId);
    const cover = target ? coverPoint(layout, self, target.state) : null;
    if (cover) {
      const d = Math.hypot(cover.x - self.x, cover.z - self.z);
      const hidden = losBlocked(layout, target!.state.x, target!.state.z, self.x, self.z);
      // Arrived and out of sight: hold there at a crawl; otherwise make for it.
      const throttle = hidden && d < COVER_HOLD_RADIUS ? 0.25 : 1;
      input = { ...input, throttle, rudder: steerTo(self, bearingTo(self, cover.x, cover.z)) };
    }
  }
  // The helm the tactics asked for, bent round whatever island is in the way.
  // Only the helm: the guns still lay on the target the tactics picked.
  const wanted = self.yaw + input.rudder * 0.6;
  const clear = avoidLand(layout, self.x, self.z, wanted, 900 + Math.abs(self.speed) * 25, spec.beamM * 0.5 + 40);
  if (clear === wanted) return input;
  return { ...input, rudder: steerTo(self, clear), throttle: Math.min(input.throttle, 0.7) };
}

function decideCourse(
  brain: BotBrain,
  self: ShipState,
  spec: ShipSpec,
  targets: readonly BotTarget[],
  tick: number,
  rng: Prng,
  spotted: ReadonlySet<string> | null,
  zones: readonly BotZone[] | null,
): ShipInput {
  if (tick >= brain.nextThink) {
    if (rng.next() < 0.25) brain.side = brain.side === 1 ? -1 : 1;
    // 8–16 s at full skill; a green bot dwells on a decision half as long again.
    brain.nextThink = tick + Math.round((160 + rng.int(160)) * (1.5 - 0.5 * brain.skill));
  }
  if (zones && zones.length > 0) chooseStation(brain, self, zones, tick);
  else brain.station = null;

  let target: BotTarget | null = null;
  let best = DETECT_RANGE;
  for (const t of targets) {
    if (!t.state.alive) continue;
    if (spotted && !spotted.has(t.id)) continue;
    const d = Math.hypot(t.state.x - self.x, t.state.z - self.z);
    if (d < best) {
      best = d;
      target = t;
    }
  }

  const gun = spec.turrets[0]!;
  if (!target) {
    if (brain.targetId !== null) {
      // It was there a moment ago. Note when, and keep the bearing.
      if (brain.lostTick < 0) brain.lostTick = tick;
      if (tick - brain.lostTick > MEMORY_TICKS) {
        brain.targetId = null;
        brain.lostTick = -1;
      } else {
        const desired = keepInside(self, brain.lostBearing);
        return {
          throttle: 0.9,
          rudder: steerTo(self, desired),
          aimBearing: brain.lostBearing,
          aimRange: 2000,
          fire: 0,
          fireTorpedoes: 0,
          repair: self.damage.fires.length > 0 || self.damage.flooding !== null ? 1 : 0,
        };
      }
    }
    if (brain.station) {
      // Nothing to fight: go and stand in the ring.
      const helm = stationHelm(brain, self);
      const bearing = keepInside(self, helm.bearing);
      return { throttle: helm.throttle, rudder: steerTo(self, bearing), aimBearing: self.yaw, aimRange: 1000, fire: 0, fireTorpedoes: 0, repair: 0 };
    }
    // No zones to sail to: advance down the lane toward where the enemy line
    // was, but never past the far edge of the arena.
    const wx = brain.advanceX * Math.min(ARENA_HALF - 600, Math.abs(self.x) + 1200);
    const bearing = keepInside(self, bearingTo(self, wx, brain.laneZ));
    return { throttle: 0.8, rudder: steerTo(self, bearing), aimBearing: self.yaw, aimRange: 1000, fire: 0, fireTorpedoes: 0, repair: 0 };
  }

  brain.targetId = target.id;
  brain.lostTick = -1;

  const toTarget = bearingTo(self, target.state.x, target.state.z);
  brain.lostBearing = toTarget;
  const distance = best;
  const hurt = self.hp < spec.hp * RETREAT_HP;
  let desired: number;
  let throttle = 1;
  if (hurt) {
    // Run: straight away from the nearest enemy, guns still trained aft.
    desired = toTarget + Math.PI;
  } else if (brain.station && distance > ZONE_BREAK_RANGE) {
    // Far enough away to be somebody else's problem. Keep the ring and keep
    // shooting: the guns below still lay on it.
    const helm = stationHelm(brain, self);
    desired = helm.bearing;
    throttle = helm.throttle;
  } else if (distance < ENGAGE_MIN) desired = toTarget + brain.side * (Math.PI * 0.75);
  else if (distance > ENGAGE_MAX) desired = toTarget + brain.side * 0.25;
  else {
    desired = toTarget + brain.side * (Math.PI / 2);
    throttle = 0.75;
  }
  desired = keepInside(self, wrapAngle(desired));

  // Lead: where the target will be when the shell arrives.
  const tof = flightTime(distance, gun.shellSpeed) ?? 0;
  const px = target.state.x + target.vx * tof;
  const pz = target.state.z + target.vz * tof;
  // The lay is out by up to `AIM_ERROR_MAX` at skill 0 and exact at 1: one
  // draw per tick, so the error wanders rather than sticks.
  const err = (1 - brain.skill) * AIM_ERROR_MAX;
  const aimBearing = wrapAngle(bearingTo(self, px, pz) + (err > 0 ? (rng.next() * 2 - 1) * err : 0));
  const aimRange = Math.hypot(px - self.x, pz - self.z);

  // Fire once at least one turret is laid within a degree or so.
  let laid = false;
  for (const t of self.turrets) {
    if (Math.abs(angleDelta(wrapAngle(self.yaw + t.yaw), aimBearing)) < 0.03) laid = true;
  }
  const inRange = aimRange < maxShellRange(gun.shellSpeed) * 0.92;

  // Torpedoes, when the hull carries them and a tube is loaded. The fish are
  // three times slower than a shell, so they need their own lead — and the
  // wire only carries one aim, so the tick a bot launches is a tick it does
  // not shoot. That is the right trade at a minute a salvo, and it is what a
  // captain would do anyway: you do not lay the guns two ways at once.
  const torpedoBearing = torpedoSolution(self, spec, target, distance);
  if (torpedoBearing !== null) {
    return {
      throttle,
      rudder: steerTo(self, wrapAngle(desired)),
      aimBearing: torpedoBearing,
      aimRange,
      fire: 0,
      fireTorpedoes: 1,
      repair: self.damage.fires.length > 0 || self.damage.flooding !== null ? 1 : 0,
    };
  }

  return {
    throttle,
    rudder: steerTo(self, wrapAngle(desired)),
    aimBearing,
    aimRange,
    // A green gunner hesitates: at skill 0 a third of the laid ticks fire.
    fire: laid && inRange && rng.next() < 0.35 + 0.65 * brain.skill ? 1 : 0,
    fireTorpedoes: 0,
    // A bot calls the repair party as soon as it burns or floods; timing it is a human skill.
    repair: self.damage.fires.length > 0 || self.damage.flooding !== null ? 1 : 0,
  };
}

/**
 * The bearing to launch a torpedo salvo on, or `null` when there is no shot
 * worth taking. Cheap first, expensive last: no tubes, no loaded mount, no
 * range — then, and only then, the lead.
 *
 * The lead itself is `torpedoLead` — the same intercept the player's own lead
 * marker is drawn from. A bot that aimed by a different rule than the one the
 * interface teaches would be a second weapon wearing the first one's name.
 */
function torpedoSolution(self: ShipState, spec: ShipSpec, target: BotTarget, distance: number): number | null {
  const mounts = spec.torpedoes;
  if (!mounts || mounts.length === 0) return null;
  const ready = readyTorpedoMounts(self);
  if (ready.length === 0) return null;

  for (const i of ready) {
    const mount = mounts[i]!;
    if (distance > mount.range * TORPEDO_MAX_FRACTION) continue;
    if (distance < mount.range * TORPEDO_MIN_FRACTION) continue;
    const lead = torpedoLead(
      self.x,
      self.z,
      target.state.x,
      target.state.z,
      target.vx,
      target.vz,
      mount.speed,
      mount.range * TORPEDO_MAX_FRACTION,
    );
    if (lead === null) continue;
    if (!torpedoMountCanBear(self, spec, i, lead.bearing)) continue;
    return lead.bearing;
  }
  return null;
}
